import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { NavLink, useLocation, Link } from "react-router-dom";
import { FaBars, FaChevronDown, FaTimes, FaSearch, FaSun, FaMoon } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";

import { DonateButton, ZakatButton } from "../../Buttons/Buttons";
import SwitchLang from "./SwitchLang";
import Icon from "./Icon";
import { Logo } from "../../Icons/Icon";
import SearchModal from "../../Modals/SearchModal";
import ImageAyat from '../../assets/Images/ayat.png';

import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { fetchAboutData } from "../../features/AboutSlice/AboutSlice";
import { useTheme } from "../../ThemeProvider";

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();

  const { about = [] } = useSelector((state) => state.about || {});

  // UI state
  const [isOpen, setIsOpen] = useState(false); // mobile main menu
  const [openSubMenu, setOpenSubMenu] = useState(null); // mobile submenu index
  const [isOpenSearch, setIsOpenSearch] = useState(false);
  const [hoveredTab, setHoveredTab] = useState(null); // For active pill highlight

  // Theme state
  const { isDark, toggleTheme } = useTheme();

  // hide on scroll
  const [isScrolling, setIsScrolling] = useState(false);
  const prevScrollYRef = useRef(0);

  const navRef = useRef(null);

  // Fetch about data (refetch on language change)
  useEffect(() => {
    dispatch(fetchAboutData());
  }, [dispatch, i18n.language]);

  // Build links
  const links = useMemo(() => {
    const whoWeAre = about.filter((item) => item.key === "who_we_are");
    const whatCanYouDo = about.filter((item) => item.key === "what_can_you_do");

    return [
      { name: t("home"), link: "/" },
      {
        name: t("about"),
        link: "/about",
        subItems: [
          {
            section: t("whoWeAre"),
            links: whoWeAre.map((item) => ({
              id: item.id,
              name: item.value,
              link: `/about/${item.id}`,
              icon: item.icon,
            })),
          },
          {
            section: t("whatCanYouDo"),
            links: whatCanYouDo.map((item) => ({
              id: item.id,
              name: item.value,
              link: `/about/${item.id}`,
              icon: item.icon,
            })),
          },
        ],
      },
      { name: t("doctors"), link: "/doctor" },
      { name: t("projects"), link: "/project" },
      { name: t("branches"), link: "/branch" },
      { name: t("unit"), link: "/unit" },
      { name: t("needZad"), link: "/need" },
    ];
  }, [about, t]);

  // Hide/show header on scroll
  useEffect(() => {
    const handleScroll = () => {
      const current = window.scrollY;
      const prev = prevScrollYRef.current;
      const scrollingDown = current > prev && current > 50;
      setIsScrolling(scrollingDown);
      prevScrollYRef.current = current;
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close menus on resize
  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= 1024) { // lg breakpoint
        setIsOpen(false);
        setOpenSubMenu(null);
      }
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Close menus on route change
  useEffect(() => {
    setIsOpen(false);
    setOpenSubMenu(null);
    setHoveredTab(null);
  }, [location.pathname]);

  // Keyboard support
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setIsOpen(false);
        setOpenSubMenu(null);
        setHoveredTab(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const toggleMenu = useCallback(() => {
    setIsOpen((v) => {
      if (v) setOpenSubMenu(null);
      return !v;
    });
  }, []);

  const toggleSubMenu = useCallback((index) => {
    setOpenSubMenu((prev) => (prev === index ? null : index));
  }, []);

  const getFirstAboutId = useCallback(() => {
    const first = about.find((it) => it.key === "who_we_are");
    return first ? first.id : null;
  }, [about]);

  const getLink = useCallback(
    (link) => {
      try {
        const parts = (link || "").split("/").filter(Boolean);
        if (parts.length >= 2 && parts[0] === "about" && parts[1]) return `/${parts.slice(0, 2).join("/")}`;
      } catch (e) {}
      const firstId = getFirstAboutId();
      return firstId ? `/about/${firstId}` : "/about";
    },
    [getFirstAboutId]
  );

  const isRtl = i18n.language === "ar" || i18n.language === "ckb";

  return (
    <header
      ref={navRef}
      className={`sticky top-0 z-50 bg-white/75 dark:bg-zinc-950/75 backdrop-blur-md border-b border-zinc-200 dark:border-zinc-900  transition-transform duration-500 py-3 px-4 lg:px-6 xl:px-10 ${
        isScrolling ? "-translate-y-full" : "translate-y-0"
      }`}
      dir={isRtl ? "rtl" : "ltr"}
    >
      <div className="max-w-[1920px] mx-auto flex items-center justify-between gap-4">
        
        {/* FLANK A: Brand / Logo */}
        <div className="flex items-center shrink-0 gap-4">
          <Link to="/" className="flex items-center gap-3">
             <Logo width="w-12 md:w-16" height="h-12 md:h-16" />
             <div className="hidden xl:flex flex-col">
                <div className="flex items-center justify-center gap-1">
                  <p className="font-bold text-lg text-[#07A024]">{t("zad")}</p>
                  
                </div>
                {/* <img src={ImageAyat} className="w-32 md:w-40 mt-1 dark:brightness-0 dark:invert opacity-90" alt="Ayat" /> */}
             </div>
          </Link>
        </div>

        {/* CENTER: Floating Navigation Pill */}
        <nav 
          className="hidden lg:flex flex-row items-center justify-center gap-x-2 w-auto max-w-max bg-zinc-100/80 dark:bg-zinc-900/60 backdrop-blur-md border border-zinc-200/80 dark:border-zinc-800/80 rounded-full px-3 py-1.5 shadow-sm relative z-50"
          onMouseLeave={() => setHoveredTab(null)}
        >
          {links.map((item, idx) => {
            const isHovered = hoveredTab === idx;
            const hasSub = !!item.subItems;
            const targetLink = hasSub ? getLink(item.link) : item.link;

            return (
              <div
                key={item.name}
                className="relative"
                onMouseEnter={() => setHoveredTab(idx)}
              >
                <NavLink
                  to={targetLink}
                  className={({ isActive }) =>
                    `relative z-10 flex items-center justify-center gap-1 xl:gap-2 px-2 xl:px-4 py-2 rounded-full whitespace-nowrap text-[13px] xl:text-sm font-medium transition-colors duration-200 ${
                      isActive || isHovered 
                        ? "text-zinc-950 dark:text-white" 
                        : "text-zinc-700 dark:text-zinc-200 hover:text-zinc-950 dark:hover:text-white"
                    }`
                  }
                >
                  <span>{item.name}</span>
                  {hasSub && (
                    <motion.div
                      animate={{ rotate: isHovered ? 180 : 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <FaChevronDown className="text-[10px] opacity-70" />
                    </motion.div>
                  )}
                </NavLink>

                {/* Animated Background Pill */}
                {isHovered && (
                  <motion.div
                    layoutId="navPillHover"
                    className="absolute top-1.5 bottom-1.5 left-1 right-1 bg-white dark:bg-zinc-800 rounded-full shadow-sm z-0"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ type: "spring", stiffness: 400, damping: 30 }}
                  />
                )}

                {/* Mega Menu Dropdown */}
                <AnimatePresence>
                  {hasSub && isHovered && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 8 }}
                      transition={{ duration: 0.2 }}
                      // CRITICAL FIX: The dropdown is nested directly in the relative parent item.
                      // Using rtl:right-0 ltr:left-0 ensures it anchors to the physical leading edge, growing inwards smoothly.
                      className="absolute top-full mt-4 w-[480px] rtl:right-0 ltr:left-0 bg-white/95 dark:bg-zinc-900/90 backdrop-blur-md border border-zinc-200 dark:border-zinc-800 shadow-xl rounded-2xl overflow-hidden z-50"
                    >
                      <div className="grid grid-cols-2 gap-6 p-4">
                        {item.subItems.map((section, sIdx) => (
                          <div 
                            key={section.section + sIdx} 
                            // CRITICAL FIX: Conditionally align sections based on script layout
                            className={`flex flex-col space-y-2 ${isRtl ? 'items-start text-right' : 'items-start text-left'}`}
                          >
                            <h3 className="text-xs font-bold text-zinc-500 dark:text-zinc-400 mb-2 uppercase tracking-wider px-3">
                              {section.section}
                            </h3>
                            <div className="flex flex-col space-y-1 w-full">
                              {section.links.map((subItem) => (
                                <NavLink 
                                  key={subItem.id || subItem.link} 
                                  to={subItem.link}
                                  // CRITICAL FIX: Group as flex with gap. Enforce `dir` property to naturally order icons on the leading edge.
                                  dir={isRtl ? 'rtl' : 'ltr'}
                                  className={`group flex items-center gap-3 p-3 w-full rounded-xl hover:bg-zinc-50 dark:hover:bg-zinc-800/50 text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white transition-all duration-200 ${isRtl ? 'text-right' : 'text-left'}`}
                                >
                                  {subItem.icon && (
                                    <div className="w-8 h-8 rounded-lg bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center shrink-0 group-hover:bg-zinc-200 dark:group-hover:bg-zinc-700 transition-colors">
                                      <img src={process.env.REACT_APP_IMG_URL + subItem.icon} className="w-5 h-5 object-contain opacity-80" alt="" />
                                    </div>
                                  )}
                                  <p className="text-sm font-medium">{subItem.name}</p>
                                </NavLink>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </nav>

        {/* FLANK B: Actions & Mobile Toggle */}
        <div className="flex items-center gap-2 lg:gap-3 xl:gap-5 shrink-0">
           
           <div className="flex items-center gap-1 xl:gap-2">
              <button 
                onClick={() => setIsOpenSearch(true)} 
                className="w-9 h-9 xl:w-10 xl:h-10 rounded-full flex items-center justify-center text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-900 transition-colors"
                aria-label="Search"
              >
                <FaSearch className="text-[14px] xl:text-[15px]" />
              </button>
              <SwitchLang />
           </div>

           <div className="hidden lg:flex items-center gap-2 xl:gap-3">
             <button
               onClick={toggleTheme}
               aria-label="Toggle Dark Mode"
               className="rounded-xl w-9 h-9 xl:w-10 xl:h-10 flex items-center justify-center transition-all bg-zinc-100/80 border border-zinc-200 hover:bg-zinc-200/80 text-indigo-600 dark:bg-zinc-900/80 dark:border-zinc-800 dark:hover:bg-zinc-800/80 dark:text-amber-400"
             >
               <AnimatePresence mode="wait" initial={false}>
                 <motion.div
                   key={isDark ? 'dark' : 'light'}
                   initial={{ scale: 0.6, rotate: -90, opacity: 0 }}
                   animate={{ scale: 1, rotate: 0, opacity: 1 }}
                   exit={{ scale: 0.6, rotate: 90, opacity: 0 }}
                   transition={{ duration: 0.2 }}
                 >
                   {isDark ? <FaSun className="text-lg" /> : <FaMoon className="text-lg" />}
                 </motion.div>
               </AnimatePresence>
             </button>
             <DonateButton link={"/t.me/baxsharani_zad"} />
             <ZakatButton link={"/zakat"} />
           </div>

           <div className="hidden xl:flex items-center border-l border-zinc-200 dark:border-zinc-800 rtl:border-l-0 rtl:border-r pl-4 rtl:pl-0 rtl:pr-4">
             <Icon />
           </div>

           {/* Mobile Hamburger */}
           <button
             onClick={toggleMenu}
             aria-label={isOpen ? t("closeMenu") || "Close menu" : t("openMenu") || "Open menu"}
             className="lg:hidden w-10 h-10 rounded-xl bg-zinc-100 dark:bg-zinc-900 flex items-center justify-center text-zinc-700 dark:text-zinc-200 transition-colors"
           >
             {isOpen ? <FaTimes /> : <FaBars />}
           </button>
        </div>

      </div>

      {/* MOBILE Navigation Drawer */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden absolute top-full left-0 right-0 mt-2 mx-4 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 shadow-2xl rounded-2xl overflow-hidden z-50"
          >
            <div className="max-h-[calc(100vh-8rem)] overflow-y-auto py-2">
              {links.map((item, idx) => {
                const hasSub = !!item.subItems;
                return (
                  <div key={item.name + idx} className="border-b border-zinc-100 dark:border-zinc-800/50 last:border-0">
                    {hasSub ? (
                      <>
                        <button
                          onClick={() => toggleSubMenu(idx)}
                          className="w-full px-6 py-4 flex justify-between items-center text-zinc-900 dark:text-white hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors"
                        >
                          <span className="font-medium">{item.name}</span>
                          <motion.div
                            animate={{ rotate: openSubMenu === idx ? 180 : 0 }}
                            transition={{ duration: 0.2 }}
                          >
                            <FaChevronDown className="text-zinc-500 dark:text-zinc-400" />
                          </motion.div>
                        </button>

                        <AnimatePresence>
                          {openSubMenu === idx && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              className="overflow-hidden bg-zinc-50 dark:bg-zinc-950/50"
                            >
                              <div className="py-2">
                                {item.subItems.map((section, sIdx) => (
                                  <div className="px-6 py-2" key={`${section.section}-${sIdx}`}>
                                    <h3 className="text-xs font-bold text-zinc-500 dark:text-zinc-500 mb-2">{section.section}</h3>
                                    {section.links.map((subItem) => (
                                      <NavLink
                                        key={subItem.id || subItem.link}
                                        to={subItem.link}
                                        onClick={() => setIsOpen(false)}
                                        className="flex items-center gap-3 py-2 text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white transition-colors"
                                      >
                                        {subItem.icon && (
                                          <img src={process.env.REACT_APP_IMG_URL + subItem.icon} className="w-5 h-5 object-contain opacity-70" alt="" />
                                        )}
                                        <span className="text-sm font-medium">{subItem.name}</span>
                                      </NavLink>
                                    ))}
                                  </div>
                                ))}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </>
                    ) : (
                      <NavLink
                        to={item.link}
                        onClick={() => setIsOpen(false)}
                        className={({ isActive }) =>
                          `block px-6 py-4 font-medium transition-colors ${
                            isActive 
                              ? "bg-zinc-100 dark:bg-zinc-800 text-zinc-900 dark:text-white" 
                              : "text-zinc-500 dark:text-zinc-400 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 hover:text-zinc-900 dark:hover:text-white"
                          }`
                        }
                      >
                        {item.name}
                      </NavLink>
                    )}
                  </div>
                );
              })}

              <div className="p-6 md:hidden flex flex-col gap-3 border-t border-zinc-100 dark:border-zinc-800/50">
                <div className="flex items-center justify-between mb-2 px-2">
                  <span className="text-zinc-600 dark:text-zinc-400 font-medium text-sm">{isDark ? "Dark Mode" : "Light Mode"}</span>
                  <button
                    onClick={toggleTheme}
                    aria-label="Toggle Dark Mode"
                    className="rounded-xl w-10 h-10 flex items-center justify-center transition-all bg-zinc-100/80 border border-zinc-200 hover:bg-zinc-200/80 text-indigo-600 dark:bg-zinc-900/80 dark:border-zinc-800 dark:hover:bg-zinc-800/80 dark:text-amber-400"
                  >
                    <AnimatePresence mode="wait" initial={false}>
                      <motion.div
                        key={isDark ? 'dark' : 'light'}
                        initial={{ scale: 0.6, rotate: -90, opacity: 0 }}
                        animate={{ scale: 1, rotate: 0, opacity: 1 }}
                        exit={{ scale: 0.6, rotate: 90, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        {isDark ? <FaSun className="text-lg" /> : <FaMoon className="text-lg" />}
                      </motion.div>
                    </AnimatePresence>
                  </button>
                </div>
                <DonateButton link={"/t.me/baxsharani_zad"} isFull={true} />
                <ZakatButton link={"/zakat"} isFull={true} />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Search Modal */}
      {isOpenSearch && (
        <SearchModal 
          isOpen={isOpenSearch} 
          onClose={() => {
            setIsOpenSearch(false);
            document.body.style.overflow = 'unset';
          }} 
        />
      )}
    </header>
  );
}
